import { Link } from 'react-router-dom';
import { CATEGORIES, NETWORK_GROUP, NETWORK_GROUP_KEY, NETWORK_PAGE_KEYS, pageKeyOf } from './constants';
import { S } from '../../shared/studioTokens';

interface CategorySidebarProps {
  counts: Record<string, number>;
  trashCount: number;
  activeCategory: string | null;
  onSelect: (key: string | null) => void;
}

export default function CategorySidebar({ counts, trashCount, activeCategory, onSelect }: CategorySidebarProps) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const networkCount = Object.entries(counts)
    .filter(([key]) => NETWORK_PAGE_KEYS.includes(pageKeyOf(key)))
    .reduce((sum, [, n]) => sum + n, 0);

  return (
    <nav style={{
      width: 220, flexShrink: 0,
      background: 'white',
      borderRight: '1px solid #e2e8f0',
      display: 'flex', flexDirection: 'column',
      paddingTop: 16,
    }}>
      {/* All */}
      <SidebarBtn
        active={activeCategory === null}
        onClick={() => onSelect(null)}
        icon="apps"
        iconColor={S.accent}
        label="Tous les prototypes"
        count={total}
      />

      <div style={{ height: 1, background: S.border, margin: '12px 16px' }} />

      {/* Pages et sous-pages */}
      {CATEGORIES.filter((c) => c.key !== 'archive' && c.key !== 'corbeille').map((cat, index, list) => {
        const isNetwork = NETWORK_PAGE_KEYS.includes(cat.key);
        const firstOfGroup = index === 0 || NETWORK_PAGE_KEYS.includes(list[index - 1].key) !== isNetwork;
        const count = Object.entries(counts)
          .filter(([key]) => pageKeyOf(key) === cat.key)
          .reduce((sum, [, n]) => sum + n, 0);
        if (count === 0 && !(isNetwork && firstOfGroup)) return null;
        const active = activeCategory === cat.key;
        return (
          <div key={cat.key}>
            {isNetwork && firstOfGroup && (
              <SidebarBtn
                active={activeCategory === NETWORK_GROUP_KEY}
                onClick={() => onSelect(NETWORK_GROUP_KEY)}
                icon={NETWORK_GROUP.icon}
                iconColor={activeCategory === NETWORK_GROUP_KEY ? NETWORK_GROUP.color : S.textMuted}
                label={NETWORK_GROUP.label}
                count={networkCount}
                countColor={activeCategory === NETWORK_GROUP_KEY ? NETWORK_GROUP.color : undefined}
              />
            )}
            {count > 0 && (<>
            <SidebarBtn
              active={active}
              onClick={() => onSelect(cat.key)}
              icon={cat.icon}
              iconColor={active ? cat.color : S.textMuted}
              label={cat.label}
              count={count}
              countColor={active ? cat.color : undefined}
              indent={isNetwork ? 1 : 0}
            />
            {(cat.subpages ?? []).map((sub) => {
              const key = `${cat.key}/${sub.key}`;
              const subCount = counts[key] ?? 0;
              if (subCount === 0) return null;
              const subActive = activeCategory === key;
              return (
                <SidebarBtn
                  key={key}
                  active={subActive}
                  onClick={() => onSelect(key)}
                  icon="subdirectory_arrow_right"
                  iconColor={subActive ? cat.color : S.textDim}
                  label={sub.label}
                  count={subCount}
                  countColor={subActive ? cat.color : undefined}
                  nested
                  indent={isNetwork ? 2 : 1}
                />
              );
            })}
            </>)}
          </div>
        );
      })}

      {/* Trash */}
      {trashCount > 0 && (
        <>
          <div style={{ height: 1, background: S.border, margin: '12px 16px' }} />
          <SidebarBtn
            active={activeCategory === 'corbeille'}
            onClick={() => onSelect('corbeille')}
            icon="delete"
            iconColor={activeCategory === 'corbeille' ? '#f87171' : S.textDim}
            label="Corbeille"
            count={trashCount}
            countColor={activeCategory === 'corbeille' ? '#f87171' : undefined}
            danger
          />
        </>
      )}

      {/* Kits link */}
      <div style={{ marginTop: 'auto', borderTop: '1px solid #e2e8f0', padding: '12px 16px' }}>
        <Link to="/kits" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 13, color: S.accentText, textDecoration: 'none', fontWeight: 500,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>inventory</span>
          Kits Nemo →
        </Link>
      </div>
    </nav>
  );
}

function SidebarBtn({ active, onClick, icon, iconColor, label, count, countColor, danger, nested, indent = 0 }: {
  active: boolean; onClick: () => void;
  icon: string; iconColor: string; label: string; count: number;
  countColor?: string; danger?: boolean; nested?: boolean; indent?: number;
}) {
  return (
    <button type="button" onClick={onClick} aria-current={active ? 'true' : undefined} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: nested ? '6px 16px' : '8px 16px', margin: `0 8px 2px ${8 + indent * 14}px`, borderRadius: 8,
      border: 'none', cursor: 'pointer', fontFamily: 'inherit',
      background: active ? (danger ? '#fef2f2' : '#ede9fe') : 'transparent',
      color: active ? (danger ? '#ef4444' : '#4338ca') : '#475569',
      fontSize: nested ? 12 : 13, fontWeight: active ? 600 : 400, textAlign: 'left',
      transition: 'background 0.12s, color 0.12s',
    }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f1f5f9'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: iconColor }}>{icon}</span>
        {label}
      </span>
      <span style={{
        fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 10,
        color: countColor ?? '#94a3b8',
        background: active ? (danger ? '#fee2e2' : '#ede9fe') : '#f8fafc',
      }}>
        {count}
      </span>
    </button>
  );
}
